import React, { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import { supabase } from '../../../../supabaseClient';
import { resetSearchingTimeout, getProfileName } from '../../../../services/api/trips-ui';
import { SERVICE_STATUSES, SERVICE_SEARCH_TIMEOUT_SEC } from '../../../../constants';
import SearchingRadarAnimation from './searching/SearchingRadarAnimation';
import SearchingDriverInfoCard from './searching/SearchingDriverInfoCard';

function SearchingDriversPanel({ service, onDriverFound, onCancel, nearbyDrivers, rate }) {
  const [elapsed, setElapsed] = useState(0);
  const [driverAssigned, setDriverAssigned] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const onCancelRef = useRef(onCancel);
  useEffect(() => { onCancelRef.current = onCancel; }, [onCancel]);

  // Timer
  useEffect(() => {
    if (timedOut || driverAssigned) return;
    const interval = setInterval(() => setElapsed(e => {
      if (e + 1 >= SERVICE_SEARCH_TIMEOUT_SEC) {
        clearInterval(interval);
        setTimedOut(true);
      }
      return e + 1;
    }), 1000);
    return () => clearInterval(interval);
  }, [timedOut, driverAssigned]);

  // Auto-cancel after timeout
  useEffect(() => {
    if (!timedOut || !service?.service_id) return;
    Swal.fire({
      icon: 'warning',
      title: 'Sin respuesta',
      text: 'No se encontró un conductor disponible en este momento. ¿Qué deseas hacer?',
      showCancelButton: true,
      confirmButtonText: 'Cancelar servicio',
      cancelButtonText: 'Seguir esperando',
      confirmButtonColor: '#EF4444',
      cancelButtonColor: '#0EABB0',
      background: '#1E293B',
      color: '#F8FAFC'
    }).then(result => {
      if (result.isConfirmed) {
        onCancelRef.current();
      } else {
        resetSearchingTimeout(service.service_id)
          .then(({ error }) => {
            if (error) console.warn('Could not reset created_at:', error.message);
            setTimedOut(false);
            setElapsed(0);
          })
          .catch(() => {
            setTimedOut(false);
            setElapsed(0);
          });
      }
    });
  }, [timedOut, service?.service_id]);

  // Realtime subscription — listen for status changes on this service
  useEffect(() => {
    if (!service?.service_id) return;

    const channel = supabase
      .channel(`service-wait-${service.service_id}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'services',
        filter: `service_id=eq.${service.service_id}`
      }, async (payload) => {
        const newStatus = payload.new?.status;
        if (newStatus === SERVICE_STATUSES.DRIVER_ASSIGNED) {
          setDriverAssigned(true);
          let driverName = 'Un conductor';
          if (payload.new?.driver_uid) {
            const { data: profile } = await getProfileName(payload.new.driver_uid);
            if (profile?.name) driverName = profile.name;
          }
          Swal.fire({
            icon: 'success',
            title: '¡Conductor encontrado!',
            html: `<strong>${driverName}</strong> aceptó tu servicio.`,
            confirmButtonColor: '#0EABB0',
            confirmButtonText: 'Ver seguimiento',
            background: '#1E293B',
            color: '#F8FAFC'
          }).then(() => {
            onDriverFound();
          });
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [service?.service_id, onDriverFound]);

  return (
    <div className="searching-panel-container p-3 rounded-4 shadow-lg" style={{ backgroundColor: '#0F172A', border: '1px solid #0EABB0' }}>
      <SearchingRadarAnimation elapsed={elapsed} vehicleType={service?.vehicle_type} />
      <SearchingDriverInfoCard service={service} rate={rate} />
    </div>
  );
}

export default SearchingDriversPanel;
